import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { CATEGORIES } from "@/data/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm({ defaultCategory = "" }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    category: defaultCategory, product_details: "", quantity: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.category || !form.product_details) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quotes`, form);
      if (res.data?.success) {
        toast.success("Quote request received! Our team will reach out within 24 hours.");
        setForm({ name: "", email: "", phone: "", company: "", category: defaultCategory, product_details: "", quantity: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/5 text-sm text-slate-900 placeholder-slate-400";
  const label = "text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 block";

  return (
    <form onSubmit={submit} data-testid="quote-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div>
        <label className={label}>Full Name *</label>
        <input data-testid="quote-name" className={field} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Rahul Sharma" />
      </div>
      <div>
        <label className={label}>Company</label>
        <input data-testid="quote-company" className={field} value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company / Organization" />
      </div>
      <div>
        <label className={label}>Email *</label>
        <input data-testid="quote-email" type="email" className={field} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
      </div>
      <div>
        <label className={label}>Phone *</label>
        <input data-testid="quote-phone" className={field} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 ..." />
      </div>
      <div className="md:col-span-2">
        <label className={label}>Product Category *</label>
        <select data-testid="quote-category" className={field} value={form.category} onChange={(e) => update("category", e.target.value)}>
          <option value="">Select a category…</option>
          {CATEGORIES.map((c) => (
            <option key={c.slug} value={c.name}>{c.name}</option>
          ))}
          <option value="Other / Multi-category">Other / Multi-category</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <label className={label}>Product Details / Specifications *</label>
        <textarea data-testid="quote-details" rows={4} className={field} value={form.product_details} onChange={(e) => update("product_details", e.target.value)} placeholder="Brand, part number, sizes, standards…" />
      </div>
      <div>
        <label className={label}>Quantity</label>
        <input data-testid="quote-quantity" className={field} value={form.quantity} onChange={(e) => update("quantity", e.target.value)} placeholder="e.g. 100 units" />
      </div>
      <div>
        <label className={label}>Delivery timeline</label>
        <input data-testid="quote-message" className={field} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Any notes or preferred delivery date" />
      </div>

      <div className="md:col-span-2 flex items-center justify-between pt-2">
        <p className="text-xs text-slate-500">We respond within <b className="text-slate-900">24 hours</b>. Your data is never shared.</p>
        <button data-testid="quote-submit" type="submit" disabled={loading} className="btn-orange inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {loading ? "Sending…" : "Send Quote Request"}
        </button>
      </div>
    </form>
  );
}
